import { createApi } from "@reduxjs/toolkit/query/react";
import axiosBaseQuery from "utils/axios/axiosBaseQuery";

export const userService = createApi({
  reducerPath: "userService",
  baseQuery: axiosBaseQuery({
    baseUrl:
      process.env.REACT_APP_API_BASE_URL + process.env.REACT_APP_API_VERSION,
  }),
  tagTypes: ["profile"],
  endpoints: (builder) => ({
    fetchUserProfile: builder.query({
      query: () => ({
        url: "/user/profile",
        method: "GET",
        providesTags: ["profile"],
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchSubscriptionPlans: builder.query({
      query: () => ({
        url: "/plans/list",
        method: "GET",
      }),
      transformResponse: (response) => response?.data || [],
    }),
    updateUserProfile: builder.mutation({
      query: (profileData) => ({
        url: "/user/update/profile",
        method: "POST",
        data: profileData,
        invalidatesTags: ["profile"],
      }),
    }),
    resetPassword: builder.mutation({
      query: (passwordData) => ({
        url: "/user/change/password",
        method: "POST",
        data: passwordData,
      }),
    }),
    createCheckoutSession: builder.mutation({
      query: (planId) => ({
        url: "/subs/create-checkout-session",
        method: "POST",
        data: { planId },
      }),
    }),
    contactUsForm: builder.mutation({
      query: (contactData) => ({
        url: "/contact",
        method: "POST",
        data: contactData,
      }),
    }),
    fetchContent: builder.query({
      query: (name) => ({
        url: `/pages/name/${name}`,
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchTimezoneList: builder.query({
      query: () => ({
        url: "/user/admin/timezone/fetch",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchUserTimezoneList: builder.query({
      query: () => ({
        url: "/user-timezone",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchHomePage: builder.query({
      query: () => ({
        url: "/home/name/HOMEPAGE",
        method: "GET",
      }),
      transformResponse: (response) => response?.data?.home,
    }),
    fetchTutorialPage: builder.query({
      query: () => ({
        url: "/tutorial/list",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchStocks: builder.query({
      query: () => ({
        url: "/stock-exchange",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    saveStockExchangeSettings: builder.mutation({
      query: (settings) => ({
        url: "/stock-exchange/saveUserSettings",
        method: "POST",
        data: { settings: settings },
      }),
    }),
    fetchUserStockSettings: builder.query({
      query: () => ({
        url: "/stock-exchange/userSettings",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchPaymentHistory: builder.query({
      query: () => ({
        url: "/pay/transaction-history",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    fetchProductDetails: builder.query({
      query: () => ({
        url: "/subs/current-plan",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
    billingPortal: builder.mutation({
      query: (settings) => ({
        url: "/subs/billing-portal",
        method: "POST",
        data: settings,
      }),
    }),
    billingPortalSwitchPlan: builder.mutation({
      query: ({ planId }) => ({
        url: `/subs/billing-portal/switch-plan/${planId}`,
        method: "POST",
      }),
    }),
    saveTimezone: builder.mutation({
      query: ({ timezoneId }) => ({
        url: "/user-timezone",
        method: "POST",
        data: { timezoneId },
      }),
    }),
    sendOtp: builder.mutation({
      query: ({ email }) => ({
        url: "/user/change/email",
        method: "POST",
        data: { email },
        invalidatesTags: ["profile"],
      }),
    }),
    verifyOtp: builder.mutation({
      query: ({ otp }) => ({
        url: "/user/verify/change/email",
        method: "POST",
        data: { otp },
        invalidatesTags: ["profile"],
      }),
    }),
    fetchFAQ: builder.query({
      query: () => ({
        url: "/faq/list",
        method: "GET",
      }),
      transformResponse: (response) => response?.data?.items || [],
    }),
    fetchRecommendedPlans: builder.query({
      query: () => ({
        url: "/plans/recommended",
        method: "GET",
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useFetchUserProfileQuery,
  useFetchSubscriptionPlansQuery,
  useUpdateUserProfileMutation,
  useResetPasswordMutation,
  useCreateCheckoutSessionMutation,
  useContactUsFormMutation,
  useFetchContentQuery,
  useFetchTimezoneListQuery,
  useFetchUserTimezoneListQuery,
  useFetchHomePageQuery,
  useFetchTutorialPageQuery,
  useFetchStocksQuery,
  useSaveStockExchangeSettingsMutation,
  useFetchUserStockSettingsQuery,
  useFetchPaymentHistoryQuery,
  useFetchProductDetailsQuery,
  useBillingPortalMutation,
  useSaveTimezoneMutation,
  useSendOtpMutation,
  useVerifyOtpMutation,
  useFetchFAQQuery,
  useFetchRecommendedPlansQuery,
  useBillingPortalUpdatePlanMutation,
  useBillingPortalSwitchPlanMutation,
} = userService;
